import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User, UserManagementUser } from '../types';
import { isSuperAdmin, hasAnyOrgRole, getUserRoleDisplayName } from '../utils/roleUtils';
import { EditUserModal } from './EditUserModal';
import { getCompletionColor } from '../utils/intakeCompletion';

interface UsersProps {
  user: User;
  filteredUsers: UserManagementUser[];
  userSearchTerm: string;
  handleUserSearchChange: (term: string) => void;
  userStatusFilter: string;
  handleUserStatusFilterChange: (filter: string) => void;
  showUserDropdown: string | null;
  setShowUserDropdown: (userId: string | null) => void;
  handleToggle2FA: (userId: string) => void;
  handleDeleteUser: (userId: string) => void;
  handleEditUser?: () => void | Promise<void>;
  handleUserStatusChange: (userId: string, status: 'active' | 'blocked') => void;
  userIntakeCompletions?: {[userId: string]: number};
  onUserClick?: (userId: string) => void;
  // Pagination props
  userCurrentPage: number;
  setUserCurrentPage: (page: number) => void;
  usersPerPage: number;
  totalUsers: number;
}

export default function Users({
  user,
  filteredUsers,
  userSearchTerm,
  handleUserSearchChange,
  userStatusFilter,
  handleUserStatusFilterChange,
  showUserDropdown,
  setShowUserDropdown,
  handleToggle2FA,
  handleDeleteUser,
  handleEditUser,
  handleUserStatusChange,
  userIntakeCompletions = {},
  onUserClick,
  // Pagination props
  userCurrentPage,
  setUserCurrentPage,
  usersPerPage,
  totalUsers
}: UsersProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedRoleTab, setSelectedRoleTab] = useState<'all' | 'super_admin' | 'admin' | 'department_manager' | 'program_manager' | 'team_manager' | 'instructor'>('all');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  // Pagination logic
  const roleFilteredUsers = selectedRoleTab === 'all' ? filteredUsers : filteredUsers.filter(u => u.role === selectedRoleTab);
  const getPaginatedUsers = () => {
    const startIndex = (userCurrentPage - 1) * usersPerPage;
    return roleFilteredUsers.slice(startIndex, startIndex + usersPerPage);
  };

  const totalPages = Math.ceil((roleFilteredUsers.length || 0) / usersPerPage);
  
  const handleEditClick = (userId: string) => {
    setEditingUserId(userId);
    setShowUserDropdown(null);
  };

  const handleCloseEditModal = () => {
    setEditingUserId(null);
  };

  const handleUserUpdated = async () => {
    // This will be called from the modal after successful update
    // The parent component should refresh the user list
    if (handleEditUser) {
      await handleEditUser();
    }
  };
  
  return (
    <div className="users-management" style={{ position: 'relative' }}>
      <div className="users-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <h2>{t('users.title')}</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="users-controls">
            <select
              value={userStatusFilter}
              onChange={(e) => handleUserStatusFilterChange(e.target.value)}
              className="user-status-filter"
            >
              <option value="all">{t('users.allUsers')}</option>
              <option value="active">{t('users.statusActive')}</option>
              <option value="blocked">{t('users.statusBlocked')}</option>
            </select>
          </div>
          {(isSuperAdmin(user) || hasAnyOrgRole(user, ['admin', 'department_manager', 'program_manager', 'team_manager'])) && (
          <button
            onClick={() => navigate('/create')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007acc',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#005a9e';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#007acc';
            }}
          >
            <span style={{ fontSize: '18px' }}>+</span>
            {t('users.addUser')}
          </button>
          )}
        </div>
      </div>
      
      {isSuperAdmin(user) && (
        <div style={{ margin: '10px 0', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: t('users.allUsers') },
            { key: 'super_admin', label: t('users.roles.super_admin') },
            { key: 'admin', label: t('users.roles.admin') },
            { key: 'department_manager', label: t('users.roles.department_manager') },
            { key: 'program_manager', label: t('users.roles.program_manager') },
            { key: 'team_manager', label: t('users.roles.team_manager') },
            { key: 'instructor', label: t('users.roles.instructor') },
          ].map(tab => (
            <button
              key={String(tab.key)}
              onClick={() => { setUserCurrentPage(1); setSelectedRoleTab(tab.key as any); }}
              style={{
                padding: '6px 10px',
                borderRadius: '14px',
                border: selectedRoleTab === tab.key ? '1px solid #007acc' : '1px solid #ddd',
                background: selectedRoleTab === tab.key ? '#e6f2fb' : '#fff',
                color: '#333',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('users.name')}</th>
              <th>{t('users.email')}</th>
              <th>{t('users.role')}</th>
              <th>{t('users.status')}</th>
              <th>{t('users.lastLogin')}</th>
              <th>{t('users.intakeCompletion') || 'Intake Completion'}</th>
              <th>{t('users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedUsers().map((userItem) => (
              <tr 
                key={userItem.id} 
                className="user-row"
                onClick={() => {
                  if (onUserClick) {
                    onUserClick(userItem.id);
                  }
                }}
                style={{ cursor: onUserClick ? 'pointer' : 'default' }}
              >
                <td className="user-name-cell">
                  <div className="user-name-with-avatar">
                    <div className="user-avatar">
                      {userItem.name.charAt(0).toUpperCase()}
                    </div>
                    <span>{userItem.name}</span>
                  </div>
                </td>
                <td className="user-email-cell">{userItem.email}</td>
                <td className="user-role-cell">
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: userItem.role === 'super_admin' ? '#e6f2fb' : '#f0f0f0',
                    color: userItem.role === 'super_admin' ? '#007acc' : '#666'
                  }}>
                    {getUserRoleDisplayName(userItem, t)}
                  </span>
                </td>
                <td className="user-status-cell" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={userItem.blocked || userItem.restricted ? 'blocked' : 'active'}
                    onChange={(e) => handleUserStatusChange(userItem.id, e.target.value as 'active' | 'blocked')}
                    className={`user-status-dropdown status-${userItem.blocked || userItem.restricted ? 'blocked' : 'active'}`}
                    disabled={userItem.id === user.id} // Prevent users from blocking themselves
                  >
                    <option value="active">{t('users.statusActive')}</option>
                    <option value="blocked">{t('users.statusBlocked')}</option>
                  </select>
                </td>
                <td className="user-login-cell">
                  {userItem.lastLoginAt 
                    ? new Date(userItem.lastLoginAt).toLocaleDateString('en-GB')
                    : t('users.never')
                  }
                </td>
                <td className="user-intake-cell">
                  {userIntakeCompletions[userItem.id] !== undefined ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '60px',
                          height: '8px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                      >
                        <div
                          style={{
                            width: `${userIntakeCompletions[userItem.id]}%`,
                            height: '100%',
                            backgroundColor: getCompletionColor(userIntakeCompletions[userItem.id]),
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                      <span style={{ 
                        fontSize: '13px', 
                        fontWeight: '500',
                        color: getCompletionColor(userIntakeCompletions[userItem.id]),
                        minWidth: '40px'
                      }}>
                        {userIntakeCompletions[userItem.id]}%
                      </span>
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: '13px' }}>-</span>
                  )}
                </td>
                <td className="user-actions-cell" onClick={(e) => e.stopPropagation()}>
                  <div className="user-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUserDropdown(
                          showUserDropdown === userItem.id ? null : userItem.id
                        );
                      }}
                      className="user-action-btn"
                    >
                      ⋯
                    </button>
                    
                    {showUserDropdown === userItem.id && (
                      <div className="user-dropdown">
                        <button onClick={() => handleEditClick(userItem.id)}>
                          {t('users.editUser')}
                        </button>
                        <button onClick={() => handleToggle2FA(userItem.id)}>
                          {userItem.twoFactorEnabled ? t('users.disable2FA') : t('users.enable2FA')}
                        </button>
                        {/* Only show delete option if user is not deleting themselves */}
                        {userItem.id !== user.id && (
                          <button onClick={() => handleDeleteUser(userItem.id)}>
                            {t('users.deleteUser')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalUsers > 0 && (
          <div className="users-pagination">
            <div className="pagination-controls">
              <button
                onClick={() => setUserCurrentPage(Math.max(1, userCurrentPage - 1))}
                disabled={userCurrentPage === 1}
                className="pagination-btn"
              >
                {i18n.language === 'he' ? '→' : '←'}
              </button>
              <span className="pagination-current">
                {userCurrentPage} / {totalPages}
              </span>
              <button
                onClick={() => setUserCurrentPage(Math.min(totalPages, userCurrentPage + 1))}
                disabled={userCurrentPage === totalPages}
                className="pagination-btn"
              >
                {i18n.language === 'he' ? '←' : '→'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUserId && (
        <EditUserModal
          userId={editingUserId}
          currentUser={user}
          onClose={handleCloseEditModal}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
}
